import { useState } from 'react'
import { ArrowLeft, Check, Info, Lock } from 'lucide-react'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageContent } from '@/components/layout/PageContent'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { usersApi } from '@/lib/api'
import type { SubscriptionTier } from '@/lib/api'
import { useRouter } from '@/routing/navigation'
import { ROUTES } from '@/routes/routes'
import useSWR from 'swr'
import { useAuth } from '@/lib/config/auth-provider'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'
import { PlanCard, type PlanCardCta } from '@/components/billing/PlanCard'
import { BILLING_LOCKED_MESSAGE } from '@/components/billing/BillingLockedTooltip'
import {
  CONTACT_SALES_URL,
  MAX_PLAN_NAME,
  PLAN_CARDS,
  type PlanCardConfig,
  type PlanCardId,
  getPlanCard,
} from '@/pages/SettingsPage/components/billing/planConfig'
import { planTierDisplayName } from '@/pages/SettingsPage/components/billing/displayNames'
import { HowCreditsWorkDialog } from '@/pages/SettingsPage/components/billing/HowCreditsWorkDialog'

// Feature comparison table

const ENTERPRISE_CARD = getPlanCard('enterprise')
const MAX_CARD = getPlanCard('max')

type FeatureValue = boolean | string | number | null

interface FeatureRow {
  id: string
  label: string
  /** Optional "?" affordance next to the row label. */
  tooltip?: string
  enterprise: FeatureValue
  max: FeatureValue
}

function numberLocale(language: string): string {
  return intlLocaleForUi(language)
}

/** Monthly price for a card. A card with no price (Max) reads "Custom". */
function priceLabel(
  card: PlanCardConfig,
  t: TFunction<'app'>,
  locale: string,
): string {
  return card.monthlyPriceUsd === null
    ? t('billing.custom')
    : `$${card.monthlyPriceUsd.toLocaleString(locale)}`
}

function featureRows(
  t: TFunction<'app'>,
  locale: string,
): Array<FeatureRow> {
  const creditsOrg =
    ENTERPRISE_CARD.includedCredits != null
      ? t('billing.plans.values.creditsOrgPool', {
          count: ENTERPRISE_CARD.includedCredits.toLocaleString(locale),
        })
      : t('billing.custom')
  return [
    {
      id: 'audience-simulation',
      label: t('billing.plans.features.audienceSimulation'),
      enterprise: t('billing.plans.values.privateUnlimited'),
      max: t('billing.plans.values.privateUnlimited'),
    },
    { id: 'studies', label: t('billing.plans.features.studies'), enterprise: true, max: true },
    { id: 'quick-studies', label: t('billing.plans.features.quickStudies'), enterprise: true, max: true },
    { id: 'interview-chats', label: t('billing.plans.features.interviewChats'), enterprise: true, max: true },
    { id: 'focus-group-chats', label: t('billing.plans.features.focusGroupChats'), enterprise: true, max: true },
    {
      id: 'pricing-model',
      label: t('billing.plans.features.pricingModel'),
      enterprise: t('billing.plans.values.orgFlat'),
      max: t('billing.plans.values.orgFlat'),
    },
    {
      id: 'credits',
      label: t('billing.plans.features.credits'),
      enterprise: creditsOrg,
      max: t('billing.plans.values.creditsCategory'),
    },
    { id: 'branded-theme', label: t('billing.plans.features.brandedTheme'), enterprise: true, max: true },
    {
      id: 'professional-services',
      label: t('billing.plans.features.professionalServices'),
      enterprise: t('billing.plans.values.dedicatedCsm'),
      max: t('billing.plans.values.adoptionPlan'),
    },
    {
      id: 'categories-supported',
      label: t('billing.plans.features.categories'),
      tooltip: t('billing.plans.features.categoriesTooltip'),
      enterprise: t('billing.plans.values.categoriesOne'),
      max: t('billing.plans.values.categoriesMultiple'),
    },
    {
      id: 'security',
      label: t('billing.plans.features.security'),
      enterprise: t('billing.plans.values.securityStandard'),
      max: t('billing.plans.values.securitySso'),
    },
    {
      id: 'price-monthly',
      label: t('billing.plans.features.priceMonthly'),
      enterprise: priceLabel(ENTERPRISE_CARD, t, locale),
      max: priceLabel(MAX_CARD, t, locale),
    },
  ]
}

function FeatureCell({ value }: { value: FeatureValue }) {
  const { t } = useTranslation('app')
  if (value === true) {
    return (
      <div className="flex justify-center">
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15">
          <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
        </div>
      </div>
    )
  }
  if (value === false || value === null) {
    return (
      <div className="flex justify-center">
        <Lock className="h-4 w-4 text-muted-foreground/40" />
      </div>
    )
  }
  if (value === 'Soon') {
    return (
      <Text size="sm" color="muted" className="text-center">
        {t('billing.plans.soon')}
      </Text>
    )
  }
  return (
    <Text size="sm" className="text-center">
      {typeof value === 'number' ? value.toLocaleString() : String(value)}
    </Text>
  )
}

/**
 * CTA state for a card. Max is always "Contact sales"; Enterprise is active for
 * anyone already on it (or on the internal tier) and otherwise renders a locked
 * start / upgrade button, since plan changes now go through customer support.
 */
function ctaKindForCard(
  card: PlanCardConfig,
  currentTier: SubscriptionTier,
  hasActiveSubscription: boolean,
): PlanCardCta['kind'] {
  if (card.contactSales) return 'contact-sales'
  if (currentTier === 'fairgen_internal') return 'active'
  if (currentTier === card.tier) return 'active'
  return hasActiveSubscription ? 'upgrade' : 'start'
}

// Page

function handleCta(_cardId: PlanCardId, _action: PlanCardCta['kind']) {
  // Intentionally empty.
}

export default function SettingsBillingPlansPage() {
  const { t, i18n } = useTranslation('app')
  const locale = numberLocale(i18n.language)
  const features = featureRows(t, locale)
  const router = useRouter()
  const [creditsModalOpen, setCreditsModalOpen] = useState(false)

  const { isAuthenticated } = useAuth()
  const { data: profile } = useSWR(
    isAuthenticated ? 'profile' : null,
    () => usersApi.getProfile(),
    { revalidateOnFocus: false },
  )

  // Skeleton-gate before reading any profile fields so we don't transiently
  // assume a tier (was previously defaulting to 'trial' here while the
  // sibling Billing tab defaulted to 'locked_free', same user, two UIs).
  if (!profile) {
    return (
      <PageLayout className="max-w-full pt-14 py-8 md:pt-8">
        <PageContent className="gap-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-96 w-full rounded-xl" />
            <Skeleton className="h-96 w-full rounded-xl" />
          </div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </PageContent>
      </PageLayout>
    )
  }

  const currentTier: SubscriptionTier = profile.subscriptionTier
  const hasActiveSubscription = profile.hasActiveSubscription ?? false

  return (
    <TooltipProvider>
      <PageLayout className="max-w-full pt-14 py-8 md:pt-8">
        <PageHeader
          className="mt-12 md:mt-0 md:items-end"
          leadingInset={false}
          leading={
            <button
              type="button"
              onClick={() => router.push(ROUTES.settingsTab('billing'))}
              data-testid="pricing-back-link"
              className="relative z-10 flex items-center gap-1.5 text-[14px] text-muted-foreground hover:text-foreground transition-colors w-fit"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('billing.plans.back')}
            </button>
          }
          title={
            <h1 className="font-display text-[2rem] md:text-[36px] leading-[1.2] tracking-tight">
              {t('billing.plans.title')}
            </h1>
          }
          description={
            <Text size="sm" color="muted">
              {t('billing.plans.creditsRefresh')}{' '}
              <button
                type="button"
                onClick={() => setCreditsModalOpen(true)}
                data-testid="how-credits-work-trigger"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                {t('billing.plans.howCreditsWork')}
              </button>
            </Text>
          }
        />

        <HowCreditsWorkDialog open={creditsModalOpen} onOpenChange={setCreditsModalOpen} />

        <PageContent className="gap-8">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PLAN_CARDS.map((card) => {
                const kind = ctaKindForCard(card, currentTier, hasActiveSubscription)
                return (
                  <div key={card.id} className="flex flex-col gap-2">
                    <PlanCard
                      config={card}
                      currentTier={currentTier}
                      cta={{ kind }}
                      onCta={handleCta}
                      ctaDisabledReason={
                        kind === 'active' || kind === 'contact-sales'
                          ? null
                          : BILLING_LOCKED_MESSAGE
                      }
                      tagline={t(`billing.plans.tagline.${card.id}`)}
                      subtitle={t(`billing.plans.subtitle.${card.id}`)}
                    />
                  </div>
                )
              })}
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {t('billing.needCustomTerms')}{' '}
              <a
                href={CONTACT_SALES_URL}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
                data-testid="pricing-talk-to-sales"
              >
                {t('billing.talkToSales')}
              </a>
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <button
              type="button"
              data-testid="pricing-compare-plans-toggle"
              onClick={() =>
                document
                  .getElementById('pricing-compare-table')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
              className="text-[14px] text-muted-foreground hover:text-foreground transition-colors text-left"
            >
              {t('billing.plans.compareTitle')}
            </button>

            <div className="overflow-x-auto -mx-4 md:mx-0">
              <div
                id="pricing-compare-table"
                className="rounded-xl border border-border overflow-hidden min-w-[400px] mx-4 md:mx-0"
              >
                <div className="grid grid-cols-3 bg-muted/50 px-5 py-3">
                  <div />
                  {PLAN_CARDS.map((card) => (
                    <Text key={card.id} size="sm" weight="medium" className="text-center">
                      {card.tier ? planTierDisplayName(card.tier) : MAX_PLAN_NAME}
                    </Text>
                  ))}
                </div>

                {features.map((feature, i) => (
                  <div
                    key={feature.id}
                    className={cn(
                      'grid grid-cols-3 px-5 py-3 items-center',
                      i % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <Text size="sm">{feature.label}</Text>
                      {feature.tooltip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={t('billing.plans.aboutFeature', { label: feature.label })}
                              data-testid={`pricing-feature-tooltip-${feature.id}`}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Info className="h-3 w-3" aria-hidden />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">
                            {feature.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </div>
                    <FeatureCell value={feature.enterprise} />
                    <FeatureCell value={feature.max} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </PageContent>
      </PageLayout>
    </TooltipProvider>
  )
}
